import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save, Mail, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import AdminPageHeader from '../../components/AdminPageHeader';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

const EmailSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [settings, setSettings] = useState({
    sendgrid_api_key: '',
    admin_email: '',
    sender_email: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axiosInstance.get('/settings/email');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching email settings:', error);
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosInstance.put('/settings/email', settings);
      toast.success('Email-Einstellungen erfolgreich gespeichert');
      fetchSettings(); // Reload to get masked key
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#E8E0D5' }}>
        <div className="max-w-4xl mx-auto">
          <p className="text-center py-12">Lädt...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 sm:py-6 lg:py-8 px-3 sm:px-4 md:px-6 lg:px-8" style={{ backgroundColor: '#F5EDE4' }}>
      <div className="max-w-4xl mx-auto">
        <AdminPageHeader 
          title="E-Mail Einstellungen" 
          subtitle="SendGrid konfigurieren für automatische E-Mail-Benachrichtigungen"
          icon={Mail}
        />

        {/* Section: E-Mail Konfiguration - Goldbraune Überschrift */}
        <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
          <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F0F8FF]">
            <Mail className="h-4 w-4 text-[#60A0D0]" />
          </div>
          <h2 className="text-base font-semibold text-white">E-Mail Konfiguration</h2>
        </div>

        {/* Main Card */}
        <Card className="rounded-none border-[#C4A084]">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <Mail className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <CardTitle>Email-Konfiguration</CardTitle>
                <CardDescription>
                  Konfigurieren Sie SendGrid für automatische Email-Benachrichtigungen
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* SendGrid API Key */}
            <div>
              <Label htmlFor="sendgrid_api_key">SendGrid API Key *</Label>
              <div className="relative mt-2">
                <Input
                  id="sendgrid_api_key"
                  type={showApiKey ? "text" : "password"}
                  value={settings.sendgrid_api_key || ''}
                  onChange={(e) => handleInputChange('sendgrid_api_key', e.target.value)}
                  placeholder="SG.xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Beginnt mit "SG." - Kostenloser Key auf{' '}
                <a 
                  href="https://sendgrid.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  sendgrid.com
                </a>
              </p>
            </div>

            {/* Admin Email */}
            <div>
              <Label htmlFor="admin_email">Admin Email *</Label>
              <Input
                id="admin_email"
                type="email"
                value={settings.admin_email || ''}
                onChange={(e) => handleInputChange('admin_email', e.target.value)}
                placeholder="ihre-email@beispiel.de"
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">
                An diese Adresse werden Bestellbenachrichtigungen gesendet
              </p>
            </div>

            {/* Sender Email */}
            <div>
              <Label htmlFor="sender_email">Absender Email *</Label>
              <Input
                id="sender_email"
                type="email"
                value={settings.sender_email || ''}
                onChange={(e) => handleInputChange('sender_email', e.target.value)}
                placeholder="noreply@ihre-domain.de"
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">
                Von dieser Adresse erhalten Kunden Emails (muss in SendGrid verifiziert sein)
              </p>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 mb-2">
                <strong>📧 So erhalten Sie Ihren SendGrid API Key:</strong>
              </p>
              <ol className="text-sm text-blue-700 space-y-1 list-decimal pl-5">
                <li>Gehen Sie zu <a href="https://sendgrid.com" target="_blank" rel="noopener noreferrer" className="underline">sendgrid.com</a> und erstellen Sie ein kostenloses Konto</li>
                <li>Navigieren Sie zu "Settings" → "API Keys"</li>
                <li>Klicken Sie auf "Create API Key"</li>
                <li>Wählen Sie "Full Access" und kopieren Sie den Key</li>
                <li>Fügen Sie den Key hier ein und speichern Sie</li>
              </ol>
              <p className="text-xs text-blue-600 mt-2">
                💡 Kostenlos: 100 Emails pro Tag
              </p>
            </div>

            {/* Feature List */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-green-800 mb-2">
                ✅ Automatische Email-Benachrichtigungen:
              </p>
              <ul className="text-sm text-green-700 space-y-1">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Bestellbestätigung an Kunde (sofort)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Admin-Benachrichtigung bei neuer Bestellung
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Versandbestätigung (manuell)
                </li>
              </ul>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t">
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-gradient-to-r from-[#C4A084] to-[#B89968] text-white"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Speichert...' : 'Einstellungen speichern'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Warning Box */}
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800">
            <strong>⚠️ Wichtig:</strong> Ohne SendGrid API Key werden keine Emails versendet. 
            Bestellungen funktionieren trotzdem, aber Kunden und Admin erhalten keine Benachrichtigungen.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailSettings;
